<template>
  <div class="pickup-display-page">
    <div class="pickup-orb orb-one"></div>
    <div class="pickup-orb orb-two"></div>

    <header class="pickup-header">
      <div class="pickup-brand">
        <div class="pickup-brand-mark">
          <img
            v-if="brandLogo"
            :src="brandLogo"
            :alt="`${stateStore.siteName} logo`"
          />
          <span v-else>{{ brandInitial }}</span>
        </div>

        <div>
          <span class="pickup-eyebrow">{{ $t("pos.pickup_screen") }}</span>
          <h1>{{ stateStore.siteName }}</h1>
          <p>{{ $t("pos.pickup_display_description") }}</p>
        </div>
      </div>

      <div class="pickup-meta">
        <article class="pickup-meta-card">
          <span>{{ $t("pos.counter") }}</span>
          <strong>{{ activeWarehouseLabel }}</strong>
          <small>{{ $t("pos.ready_orders_refresh_automatically") }}</small>
        </article>
        <article class="pickup-meta-card">
          <span>{{ $t("pos.last_refresh") }}</span>
          <strong>{{ liveTime }}</strong>
          <small>{{ liveDate }}</small>
        </article>
        <article class="pickup-meta-card">
          <span>{{ $t("pos.ready_orders") }}</span>
          <strong>{{ completedOrders.length }}</strong>
          <small>{{ $t("pos.showing_latest_completed_tickets") }}</small>
        </article>
      </div>
    </header>

    <main class="pickup-main">
      <section v-if="completedOrders.length" class="pickup-grid">
        <article
          v-for="order in completedOrders"
          :key="order.id"
          class="pickup-card"
        >
          <span class="pickup-card-label">{{ $t("pos.waiting_number") }}</span>
          <strong>#{{ getQueueLabel(order) }}</strong>
          <h2>{{ order.reference_no }}</h2>
          <p>{{ order.customer_name || $t("pos.walk_in_customer") }}</p>
          <small>
            {{ $t("pos.ready_at") }} {{ formatDateTime(order.queue_completed_at || order.created_at) }}
          </small>
        </article>
      </section>

      <section v-else class="pickup-empty-state">
        <div class="pickup-empty-mark">
          <i class="ri-cup-line"></i>
        </div>
        <h2>{{ $t("pos.no_completed_orders_yet") }}</h2>
        <p>{{ $t("pos.completed_invoices_will_appear") }}</p>
      </section>
    </main>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
} from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import posService, { type PosQueueSale } from "../../services/pos";
import warehouseService, { type Warehouse } from "../../services/warehouse";
import stateStore from "../../utils/store";
import { buildAssetUrl } from "../../utils/settings";
import { formatQueueLabel } from "../../utils/posQueue";
import { useCurrentUser } from "../../composables/useCurrentUser";

export default defineComponent({
  name: "PosPickupDisplayPage",
  setup() {
    const { t, locale } = useI18n();
    const route = useRoute();
    const { fetchCurrentUser } = useCurrentUser();

    const completedOrders = ref<PosQueueSale[]>([]);
    const warehouses = ref<Warehouse[]>([]);
    const activeWarehouseId = ref<number | null>(null);
    const now = ref(new Date());

    let clockTimer: number | null = null;
    let refreshTimer: number | null = null;
    const activeLocale = computed(() =>
      String(locale.value || "en").toLowerCase().startsWith("km")
        ? "km-KH"
        : "en-US"
    );

    const brandLogo = computed(() => buildAssetUrl(stateStore.logo || ""));
    const brandInitial = computed(() =>
      String(stateStore.siteName || "C").trim().charAt(0).toUpperCase()
    );

    const activeWarehouseLabel = computed(() => {
      if (!activeWarehouseId.value) {
        return t("pos.all_counters");
      }

      return (
        warehouses.value.find((warehouse) => warehouse.id === activeWarehouseId.value)
          ?.name || t("pos.selected_counter")
      );
    });

    const liveTime = computed(() =>
      now.value.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    );

    const liveDate = computed(() =>
      now.value.toLocaleDateString(activeLocale.value, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    );

    const fetchWarehouses = async () => {
      try {
        warehouses.value = await warehouseService.getWarehouses();
      } catch (error) {
        console.error("Unable to load warehouses:", error);
      }
    };

    const fetchCompletedOrders = async () => {
      try {
        const data = await posService.getQueueOrders({
          warehouse_id: activeWarehouseId.value || undefined,
          status: "completed",
          limit: 12,
        });

        completedOrders.value = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Unable to load completed pickup orders:", error);
      }
    };

    const getQueueLabel = (order: PosQueueSale) =>
      order.queue_label || formatQueueLabel(order.queue_number);

    const formatDateTime = (value?: string | null) => {
      if (!value) return "-";

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";

      return date.toLocaleTimeString(activeLocale.value, {
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    onMounted(async () => {
      const warehouseFromRoute = Number(route.query.warehouse_id || 0) || null;
      const currentUser = await fetchCurrentUser(true);

      activeWarehouseId.value =
        warehouseFromRoute || Number(currentUser?.warehouse_id || 0) || null;

      await fetchWarehouses();
      await fetchCompletedOrders();

      clockTimer = window.setInterval(() => {
        now.value = new Date();
      }, 1000);

      refreshTimer = window.setInterval(() => {
        fetchCompletedOrders();
      }, 15000);
    });

    onUnmounted(() => {
      if (clockTimer !== null) {
        window.clearInterval(clockTimer);
      }

      if (refreshTimer !== null) {
        window.clearInterval(refreshTimer);
      }
    });

    return {
      completedOrders,
      stateStore,
      brandLogo,
      brandInitial,
      activeWarehouseLabel,
      liveTime,
      liveDate,
      getQueueLabel,
      formatDateTime,
    };
  },
});
</script>

<style scoped>
.pickup-display-page {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  padding: 18px;
  background:
    radial-gradient(circle at top left, rgba(249, 115, 22, 0.18), transparent 35%),
    radial-gradient(circle at bottom right, rgba(14, 165, 233, 0.18), transparent 30%),
    linear-gradient(135deg, #fff9f2 0%, #f8fafc 48%, #eff6ff 100%);
  color: #0f172a;
}

.pickup-orb {
  position: absolute;
  border-radius: 999px;
  filter: blur(10px);
  opacity: 0.55;
}

.orb-one {
  top: 48px;
  right: -40px;
  width: 240px;
  height: 240px;
  background: rgba(251, 146, 60, 0.2);
}

.orb-two {
  bottom: 72px;
  left: -60px;
  width: 260px;
  height: 260px;
  background: rgba(59, 130, 246, 0.16);
}

.pickup-header,
.pickup-main {
  position: relative;
  z-index: 1;
}

.pickup-header {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 14px;
}

.pickup-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pickup-brand-mark {
  width: 58px;
  height: 58px;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #f97316;
  font-size: 22px;
  font-weight: 800;
}

.pickup-brand-mark img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pickup-eyebrow {
  display: inline-block;
  margin-bottom: 4px;
  color: #ea580c;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.pickup-brand h1 {
  margin: 0;
  font-size: clamp(1.45rem, 2vw, 2.1rem);
  line-height: 1.05;
}

.pickup-brand p {
  margin: 6px 0 0;
  max-width: 420px;
  color: #475569;
  font-size: 12px;
}

.pickup-meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  min-width: 400px;
}

.pickup-meta-card {
  padding: 11px 13px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(14px);
}

.pickup-meta-card span,
.pickup-meta-card strong,
.pickup-meta-card small {
  display: block;
}

.pickup-meta-card span {
  color: #64748b;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.pickup-meta-card strong {
  margin: 6px 0 3px;
  font-size: 16px;
}

.pickup-meta-card small {
  color: #475569;
  font-size: 11px;
}

.pickup-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.pickup-card {
  padding: 14px 12px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.09);
  backdrop-filter: blur(14px);
}

.pickup-card-label,
.pickup-card strong,
.pickup-card h2,
.pickup-card p,
.pickup-card small {
  display: block;
}

.pickup-card-label {
  color: #f97316;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.pickup-card strong {
  margin: 8px 0 4px;
  font-size: clamp(1.7rem, 2.7vw, 2.3rem);
  line-height: 1;
  color: #0f172a;
}

.pickup-card h2 {
  margin: 0 0 6px;
  font-size: 15px;
  color: #1e293b;
  line-height: 1.2;
}

.pickup-card p {
  margin: 0 0 10px;
  color: #475569;
  font-size: 12px;
  line-height: 1.35;
}

.pickup-card small {
  color: #64748b;
  font-size: 10px;
}

.pickup-empty-state {
  min-height: 62vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
}

.pickup-empty-mark {
  width: 96px;
  height: 96px;
  border-radius: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.08);
  margin-bottom: 16px;
}

.pickup-empty-mark i {
  font-size: 42px;
  color: #f97316;
}

.pickup-empty-state h2 {
  margin-bottom: 10px;
  font-size: 28px;
}

.pickup-empty-state p {
  max-width: 500px;
  color: #475569;
  font-size: 15px;
}

.pickup-main {
  min-height: 0;
}

@media (max-width: 1500px) {
  .pickup-header {
    flex-direction: column;
  }

  .pickup-meta {
    min-width: 0;
  }
}

@media (min-width: 1440px) {
  .pickup-grid {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}

@media (min-width: 1920px) {
  .pickup-grid {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
}

@media (max-width: 1100px) {
  .pickup-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .pickup-meta {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 860px) {
  .pickup-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .pickup-display-page {
    padding: 14px;
  }

  .pickup-grid {
    grid-template-columns: 1fr;
  }

  .pickup-brand {
    align-items: flex-start;
  }

  .pickup-brand h1 {
    font-size: 28px;
  }
}
</style>
